import { memo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { UserPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { FriendSuggestion } from "@/utils/friendSuggestions";
import { PremiumHatOverlay } from "@/components/profile/PremiumHatOverlay";
import { PremiumBadge } from "@/components/profile/PremiumBadge";
import { LegendaryGlow } from "@/components/profile/LegendaryGlow";

interface SuggestionCardMinimalProps {
  suggestion: FriendSuggestion;
  onAddFriend: (userId: string) => void;
  isAdding?: boolean;
}

export const SuggestionCardMinimal = memo(function SuggestionCardMinimal({ 
  suggestion, 
  onAddFriend, 
  isAdding
}: SuggestionCardMinimalProps) {
  const navigate = useNavigate();

  return (
    <Card className="p-3 hover:bg-accent/50 transition-colors">
      <div className="flex items-center gap-3">
        <LegendaryGlow isLegendary={(suggestion as any).event_count >= 100} size="md">
          <div className="relative">
            <Avatar 
              className="w-10 h-10 cursor-pointer"
              onClick={() => navigate(`/profile/${suggestion.userId}`)}
            >
              <AvatarImage src={suggestion.avatar_url || ""} />
              <AvatarFallback>
                {suggestion.username?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <PremiumHatOverlay 
              isPremium={suggestion.subscription_tier === 'premium' && suggestion.subscription_status === 'active'}
              isFoundingMember={suggestion.is_founding_member}
              size="sm"
            />
          </div>
        </LegendaryGlow>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h3 
              className="font-medium text-sm truncate cursor-pointer hover:text-primary"
              onClick={() => navigate(`/profile/${suggestion.userId}`)}
            >
              {suggestion.full_name}
            </h3>
            {(suggestion.subscription_tier === 'premium' && suggestion.subscription_status === 'active') && (
              <PremiumBadge isFoundingMember={suggestion.is_founding_member} size="sm" />
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            @{suggestion.username}
          </p>
        </div>

        <Button
          size="sm"
          onClick={() => onAddFriend(suggestion.userId)}
          disabled={isAdding}
          className="shrink-0 h-8"
        >
          <UserPlus className="w-4 h-4 mr-1" />
          Add Friend
        </Button>
      </div>
    </Card>
  );
});
